import { lookupSession } from "@/config";
import type { Session } from "@/context/UserContext";

let cachedSession: Session | null | undefined;
let sessionPromise: Promise<Session | null> | null = null;
/** Bumped on every invalidate so in-flight fetches cannot repopulate after sign-out. */
let cacheGeneration = 0;

export function currentCacheGeneration(): number {
  return cacheGeneration;
}

/** Thrown when `/auth/session` cannot be reached — not the same as signed-out. */
export class SessionUnavailableError extends Error {
  constructor() {
    super("session_unavailable");
    this.name = "SessionUnavailableError";
  }
}

/**
 * Resolves the session once and shares the result with the router and context.
 *
 * `beforeLoad` guards and `SessionProvider` both call this so a dashboard route
 * can redirect to `/login` before the shell paints, without a second round trip.
 *
 * A transient API failure (502 / network) throws `SessionUnavailableError` and
 * does **not** cache `null` — that was why an ALB blip after a heavy read looked
 * like a sudden logout on the next navigation or refresh.
 */
export async function resolveSession(): Promise<Session | null> {
  if (cachedSession !== undefined) return cachedSession;

  const generation = cacheGeneration;

  if (!sessionPromise) {
    sessionPromise = lookupSession<Session>().then((result) => {
      /* A sign-out (or another invalidate) landed while this request was in flight.
         Writing the result would put a still-valid cookie session back into the
         cache; `/login`'s beforeLoad would then bounce the operator straight
         back to the dashboard and logout would look broken. */
      if (generation !== cacheGeneration) {
        sessionPromise = null;
        return cachedSession !== undefined ? cachedSession : null;
      }

      if (result.kind === "unavailable") {
        sessionPromise = null;
        throw new SessionUnavailableError();
      }

      cachedSession = result.kind === "session" ? result.data : null;
      sessionPromise = null;
      return cachedSession;
    });
  }

  return sessionPromise;
}

/**
 * Like `resolveSession`, but retries briefly when the API is unreachable so a
 * cold or restarting backend does not flash the sign-in screen.
 */
export async function resolveSessionWithRetry(
  attempts = 3,
  delayMs = 600,
): Promise<Session | null> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await resolveSession();
    } catch (error) {
      if (!(error instanceof SessionUnavailableError)) throw error;
      if (attempt >= attempts - 1) return null;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return null;
}

/** Synchronous read after `resolveSession` has completed. */
export function getCachedSession(): Session | null | undefined {
  return cachedSession;
}

export function setCachedSession(session: Session | null): void {
  cachedSession = session;
  sessionPromise = null;
}

export function invalidateSessionCache(): void {
  cacheGeneration += 1;
  cachedSession = undefined;
  sessionPromise = null;
}

export function rememberReturnTo(pathname: string, searchStr: string): void {
  sessionStorage.setItem("console:returnTo", `${pathname}${searchStr ?? ""}`);
}

export function clearReturnTo(): void {
  sessionStorage.removeItem("console:returnTo");
}

export function consumeReturnTo(): string {
  const from = sessionStorage.getItem("console:returnTo");
  sessionStorage.removeItem("console:returnTo");
  return from && from.startsWith("/") && !from.startsWith("//") ? from : "/dashboard";
}
